/**
 * Learn Art History hero slideshow on Artist Corner (DashboardLanding).
 * Prefetchable + session-cached so the dashboard can paint artwork without waiting on Firestore twice.
 */

import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { resolveImageUrl } from './urlResolver';

export const JOURNEY_SLIDES_TARGET_MIN = 15;
export const JOURNEY_SLIDES_CAP = 40;

export const JOURNEY_HERO_SESSION_PREFIX = 'artmosphere:dashboard:journey-hero:v1:';

const VERIFIED_STORAGE_PREFIX = 'verified-artworks/';
const VERIFIED_MEDIA_BUCKET = 'artmosphere-89b64.firebasestorage.app';

function buildVerifiedMediaUrlFromPath(storagePath = '') {
  const p = String(storagePath || '').trim();
  if (!p.startsWith(VERIFIED_STORAGE_PREFIX)) return '';
  return `https://firebasestorage.googleapis.com/v0/b/${VERIFIED_MEDIA_BUCKET}/o/${encodeURIComponent(p)}?alt=media`;
}

export function pickArtHistoryImageUrl(data = {}) {
  const verified = String(data.verifiedImageUrl || '').trim();
  const fromPath = buildVerifiedMediaUrlFromPath(data.verifiedImageStoragePath || '');
  const legacy = (data.imageUrl || data.originalImageUrl || data.coverUrl || '').trim();
  for (const raw of [verified, fromPath, legacy]) {
    if (!raw) continue;
    const url = resolveImageUrl(raw) || raw;
    if (url) return url;
  }
  return '';
}

export function readJourneySlidesSessionCache(uid = '') {
  if (!uid) return [];
  try {
    const raw = sessionStorage.getItem(`${JOURNEY_HERO_SESSION_PREFIX}${uid}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => s && typeof s.url === 'string' && /^https?:\/\//i.test(s.url));
  } catch {
    return [];
  }
}

export function persistJourneySlidesSessionCache(uid, slides) {
  if (!uid || !Array.isArray(slides)) return;
  try {
    sessionStorage.setItem(`${JOURNEY_HERO_SESSION_PREFIX}${uid}`, JSON.stringify(slides));
  } catch (_) {}
}

export function clearJourneySlidesSessionCache(uid) {
  if (!uid) return;
  try {
    sessionStorage.removeItem(`${JOURNEY_HERO_SESSION_PREFIX}${uid}`);
  } catch (_) {}
}

export async function fetchUserArtworksByUid(uid) {
  if (!uid) return [];
  try {
    const [artworksSnap, sharedSnap] = await Promise.all([
      getDocs(query(collection(db, 'artworks'), where('uid', '==', uid))),
      getDocs(query(collection(db, 'sharedArtworks'), where('uid', '==', uid))),
    ]);
    const fromArtworks = artworksSnap.docs.map((d) => ({ id: d.id, ...d.data(), source: 'artworks' }));
    const fromShared = sharedSnap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        title: data.artworkTitle || data.title || '',
        imageUrl: data.artworkUrl || data.imageUrl || '',
        technique: data.artworkTechnique || data.technique || '',
        inspiration: data.artworkInspiration || data.inspiration || '',
        source: 'sharedArtworks',
      };
    });
    return [...fromArtworks, ...fromShared]
      .map((a) => ({
        ...a,
        imageUrl: (a.artworkUrl || a.imageUrl || '').trim(),
      }))
      .filter((a) => a.imageUrl);
  } catch (err) {
    console.error('[dashboardJourneyHero] fetchUserArtworksByUid:', err);
    return [];
  }
}

const journeyFetchInFlight = new Map();

async function fetchJourneyHeroSlidesUncached(uid) {
  const curated = [];
  try {
    const q = query(
      collection(db, 'artHistoryArtworks'),
      where('levelType', '==', 'Level 1'),
      limit(100)
    );
    const snap = await getDocs(q);
    const seenUrls = new Set();
    snap.docs.forEach((d) => {
      const data = d.data();
      const url = pickArtHistoryImageUrl(data);
      if (!url || seenUrls.has(url)) return;
      seenUrls.add(url);
      curated.push({ id: `lah-l1-${d.id}`, url });
    });
  } catch (err) {
    console.error('[dashboardJourneyHero] fetchJourneyHeroSlides (Level 1):', err);
  }

  for (let i = curated.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [curated[i], curated[j]] = [curated[j], curated[i]];
  }

  let slides = curated.slice(0, JOURNEY_SLIDES_CAP);
  const urlSet = new Set(slides.map((s) => s.url));

  if (slides.length < JOURNEY_SLIDES_TARGET_MIN) {
    const userList = await fetchUserArtworksByUid(uid);
    for (const a of userList) {
      if (slides.length >= JOURNEY_SLIDES_CAP) break;
      const raw = (a.artworkUrl || a.imageUrl || '').trim();
      if (!raw) continue;
      const url = resolveImageUrl(raw) || raw;
      if (urlSet.has(url)) continue;
      urlSet.add(url);
      slides.push({ id: `${a.id}-${a.source || 'u'}`, url });
    }
  }

  return slides;
}

export function fetchJourneyHeroSlides(uid) {
  if (!uid) return Promise.resolve([]);
  const existing = journeyFetchInFlight.get(uid);
  if (existing) return existing;
  const p = fetchJourneyHeroSlidesUncached(uid).finally(() => {
    journeyFetchInFlight.delete(uid);
  });
  journeyFetchInFlight.set(uid, p);
  return p;
}

const prefetchInFlight = new Map();

/** Fire-and-forget: warms sessionStorage + browser cache while user is on any route. */
export function prefetchJourneyHeroSlides(uid) {
  if (!uid) return;
  if (readJourneySlidesSessionCache(uid).length) return;
  if (prefetchInFlight.has(uid)) return;
  const p = fetchJourneyHeroSlides(uid)
    .then((slides) => {
      if (slides.length) persistJourneySlidesSessionCache(uid, slides);
      slides.slice(0, 12).forEach((s) => {
        try {
          const img = new Image();
          img.decoding = 'async';
          img.src = s.url;
        } catch (_) {}
      });
    })
    .finally(() => {
      prefetchInFlight.delete(uid);
    });
  prefetchInFlight.set(uid, p);
}
